import { useEffect, useMemo, useState } from "react";
import "../../styles/analytics-dashboard.css";

const API_BASE_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000"
    : process.env.REACT_APP_API_BASE_URL ||
      "https://apti-backend-8fhm.onrender.com";

// Matches the current Admin university-key storage used by Programme Management.
const ADMIN_API_KEY = process.env.REACT_APP_ADMIN_API_KEY;
const ROWS_PER_PAGE = 10;

const PERSONALITY_LABELS = {
  analytical_thinker: "Analytical Thinker",
  versatile_collaborator: "Versatile Collaborator",
  strategic_planner: "Strategic Planner",
  creative_explorer: "Creative Explorer",
  practical_operator: "Practical Operator",
  independent_problem_solver: "Independent Problem Solver"
};

function getPersonalityLabel(value) {
  if (!value || typeof value !== "string") return "Unavailable";
  return PERSONALITY_LABELS[value] || value.replace(/_/g, " ");
}

function parseFits(value) {
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(
    (fit) =>
      fit &&
      typeof fit === "object" &&
      Number.isFinite(fit.percentage)
  );
}

function getHighestCompatibility(fits) {
  const validFits = parseFits(fits);
  if (validFits.length === 0) {
    return { percentage: null, level: "Unavailable" };
  }
  const highest = validFits.reduce((best, fit) =>
    fit.percentage > best.percentage ? fit : best
  );
  return {
    percentage: highest.percentage,
    level:
      typeof highest.level === "string" && highest.level.trim()
        ? highest.level.trim()
        : "Unavailable"
  };
}

function formatAssessmentDate(value) {
  if (!value) return "Unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unavailable";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
}

export default function AnalyticsDashboard() {
  const [results, setResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!ADMIN_API_KEY) {
      setError("The Admin university API key is not configured.");
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    async function loadAssessments() {
      try {
        const response = await fetch(`${API_BASE_URL}/api/results`, {
          headers: { "x-api-key": ADMIN_API_KEY },
          signal: controller.signal
        });
        const payload = await response.json().catch(() => ({}));

        if (response.status === 401) {
          throw new Error("The Admin university API key is invalid.");
        }
        if (!response.ok) {
          throw new Error(payload.error || "Assessment records could not be loaded.");
        }
        setResults(Array.isArray(payload.data) ? payload.data : []);
      } catch (requestError) {
        if (requestError.name !== "AbortError") {
          setError(requestError.message || "Assessment records could not be loaded.");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadAssessments();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const preparedResults = useMemo(
    () =>
      results.map((result) => ({
        ...result,
        personalityLabel: getPersonalityLabel(result.personality_type),
        topRecommendation:
          typeof result.top_program === "string" && result.top_program.trim()
            ? result.top_program.trim()
            : "Unavailable",
        compatibility: getHighestCompatibility(result.fits),
        assessmentDate: formatAssessmentDate(result.created_at)
      })),
    [results]
  );

  const personalityDistribution = useMemo(() => {
    const counts = new Map();
    preparedResults.forEach((result) => {
      counts.set(result.personalityLabel, (counts.get(result.personalityLabel) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [preparedResults]);

  const recommendationDistribution = useMemo(() => {
    const counts = new Map();
    preparedResults.forEach((result) => {
      if (result.topRecommendation === "Unavailable") return;
      counts.set(result.topRecommendation, (counts.get(result.topRecommendation) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [preparedResults]);

  const compatibilityCounts = useMemo(() => {
    const counts = { High: 0, Moderate: 0, Low: 0, Unavailable: 0 };
    preparedResults.forEach(({ compatibility }) => {
      const level = compatibility.level;
      if (Object.prototype.hasOwnProperty.call(counts, level)) counts[level] += 1;
      else counts.Unavailable += 1;
    });
    return counts;
  }, [preparedResults]);

  const filteredResults = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return preparedResults;
    return preparedResults.filter((result) =>
      [result.id, result.personalityLabel, result.topRecommendation, result.compatibility.level, result.assessmentDate]
        .some((value) => String(value ?? "").toLowerCase().includes(query))
    );
  }, [preparedResults, searchTerm]);

  const totalPages = Math.ceil(filteredResults.length / ROWS_PER_PAGE);
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const paginatedResults = filteredResults.slice(startIndex, startIndex + ROWS_PER_PAGE);

  if (loading) {
    return <div className="assessment-state">Loading assessment records…</div>;
  }
  if (error) {
    return (
      <div className="assessment-state assessment-state--error" role="alert">
        <strong>Assessment records are unavailable.</strong>
        <span>{error}</span>
      </div>
    );
  }

  const total = preparedResults.length;

  return (
    <div className="assessment-page">
      <h1 className="sr-only">Assessment Analytics</h1>

      <section className="assessment-summary" aria-label="Compatibility overview">
        <article><span>Total records</span><strong>{total}</strong></article>
        <article className="is-high"><span>High compatibility</span><strong>{compatibilityCounts.High}</strong></article>
        <article className="is-moderate"><span>Moderate compatibility</span><strong>{compatibilityCounts.Moderate}</strong></article>
        <article className="is-low"><span>Low compatibility</span><strong>{compatibilityCounts.Low}</strong></article>
        <article><span>Compatibility unavailable</span><strong>{compatibilityCounts.Unavailable}</strong></article>
      </section>

      <section className="assessment-distributions">
        <article className="assessment-card">
          <header><h2>Personality-result distribution</h2></header>
          {personalityDistribution.length === 0 ? (
            <p className="assessment-empty">No personality results are available.</p>
          ) : (
            <div className="distribution-list">
              {personalityDistribution.map(([label, count]) => (
                <div key={label}>
                  <span><strong>{label}</strong><small>{count} record{count === 1 ? "" : "s"}</small></span>
                  <div><i style={{ width: `${(count / total) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </article>

        <article className="assessment-card">
          <header><h2>Top-recommendation distribution</h2></header>
          {recommendationDistribution.length === 0 ? (
            <p className="assessment-empty">No top recommendations are available.</p>
          ) : (
            <div className="recommendation-list">
              {recommendationDistribution.map(([name, count]) => (
                <div key={name}><span>{name}</span><strong>{count}</strong></div>
              ))}
            </div>
          )}
        </article>
      </section>

      <section className="assessment-card assessment-records-card">
        <header className="assessment-records-header">
          <div><h2>Assessment Records</h2><p>Scoped records returned for the authenticated university.</p></div>
          <label className="assessment-search">
            <span className="sr-only">Search assessment records</span>
            <input type="search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search assessment records…" />
          </label>
        </header>

        {preparedResults.length === 0 ? (
          <p className="assessment-empty">No assessment history is available for this university.</p>
        ) : filteredResults.length === 0 ? (
          <p className="assessment-empty">No assessment records match this search.</p>
        ) : (
          <div className="assessment-table-wrap">
            <table className="assessment-table">
              <thead><tr><th>Assessment ID</th><th>Assessment Date</th><th>Personality Result</th><th>Top Recommendation</th><th>Compatibility</th></tr></thead>
              <tbody>
                {paginatedResults.map((result) => {
                  const levelClass = result.compatibility.level.toLowerCase();
                  return (
                    <tr key={result.id}>
                      <td><code>{result.id ?? "Unavailable"}</code></td>
                      <td>{result.assessmentDate}</td>
                      <td>{result.personalityLabel}</td>
                      <td>{result.topRecommendation}</td>
                      <td>
                        {result.compatibility.percentage == null ? (
                          <span className="compatibility-unavailable">Unavailable</span>
                        ) : (
                          <span className={`compatibility-badge is-${levelClass}`}>
                            {Math.round(result.compatibility.percentage)}% · {result.compatibility.level}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <footer className="assessment-pagination">
          <span>Showing {filteredResults.length ? startIndex + 1 : 0}–{Math.min(startIndex + paginatedResults.length, filteredResults.length)} of {filteredResults.length} records</span>
          <div>
            <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}>Previous</button>
            <span>Page {currentPage} of {Math.max(totalPages, 1)}</span>
            <button type="button" disabled={currentPage >= totalPages || totalPages === 0} onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}>Next</button>
          </div>
        </footer>
      </section>
    </div>
  );
}
